import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import type { Tag, TagFolder } from '../store/tags/tagsApi';
import {
  useGetTagsWithFoldersQuery,
  useCreateTagMutation,
  useUpdateTagMutation,
  useDeleteTagMutation,
  useCreateFolderMutation,
  useUpdateFolderMutation,
  useDeleteFolderMutation,
} from '../store/tags/tagsApi';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTags, faPlus, faFolderPlus, faFolder, faSearch, faCheck } from '@fortawesome/free-solid-svg-icons';
import { TagBadge } from '../components/ui/TagBadge';
import { SlidingPills } from '../components/ui/SlidingPills';
import { GhostSelect } from '../components/ui/GhostSelect';
import BtnTaratic from '../components/ui/BtnTaratic';
import { useAppSelector } from '../store/hooks';
import { useNotifications } from '../components/ui/notifications';
import { useDeleteConfirmation } from '../components/ui/DeleteConfirmationProvider';

type DragItem = { kind: 'tag' | 'folder'; id: number };

interface FolderTree {
  folder: TagFolder;
  children: FolderTree[];
  tags: Tag[];
}

function buildTree(
  folders: TagFolder[],
  tags: Tag[]
): { roots: FolderTree[]; byId: Map<number, FolderTree> } {
  const byId = new Map<number, FolderTree>();
  for (const f of folders) {
    byId.set(Number(f.id), { folder: f, children: [], tags: [] });
  }
  const roots: FolderTree[] = [];
  for (const f of folders) {
    const node = byId.get(Number(f.id))!;
    const pid = f.parent_id == null ? null : Number(f.parent_id);
    if (pid == null || !byId.has(pid)) roots.push(node);
    else byId.get(pid)!.children.push(node);
  }
  for (const t of tags) {
    const fid = t.folder_id == null ? null : Number(t.folder_id);
    if (fid != null && byId.has(fid)) byId.get(fid)!.tags.push(t);
  }
  return { roots, byId };
}

function isDescendant(folderId: number, targetId: number, byId: Map<number, FolderTree>): boolean {
  if (folderId === targetId) return true;
  const node = byId.get(folderId);
  if (!node) return false;
  for (const child of node.children) {
    if (isDescendant(Number(child.folder.id), targetId, byId)) return true;
  }
  return false;
}

const DropZone: React.FC<{
  onDrop: (ev: React.DragEvent) => void;
  onDragOver?: (ev: React.DragEvent) => void;
  onDragLeave?: (ev: React.DragEvent) => void;
  active?: boolean;
  label: string;
}> = ({ onDrop, onDragOver, onDragLeave, active, label }) => {
  return (
    <div data-ui="tags-config-page-region-001"
      onDrop={onDrop}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      className={`p-3.5 border-2 border-dashed rounded-2xl mb-3 ${active ? 'border-blue-400 bg-blue-900/30 text-blue-200 border-blue-500/50' : 'border-white/20 bg-black/10 text-white/80'
        }`}
    >
      <span className="text-sm text-white/70">{label}</span>
    </div>
  );
};

const TagPill: React.FC<{
  tag: Tag;
  draggable?: boolean;
  onMove?: (tag: Tag) => void;
  onEdit?: (tag: Tag) => void;
  onDelete?: (tag: Tag) => void;
}> = ({ tag, draggable = true, onMove, onEdit, onDelete }) => {
  const { t } = useTranslation();
  return (
    <TagBadge
      tag={tag}
      size="sm"
      className="mr-2 mb-2"
      data-tag-id={String(tag.id)}
      draggable={draggable}
      onDragStart={(e: any) => {
        e.dataTransfer.setData('text/plain', `tag:${Number(tag.id)}`);
        e.dataTransfer.effectAllowed = 'move';
      }}
      prefixChildren={
        <span className="flex items-center justify-center text-white/40 cursor-grab">
          <svg className="w-3 h-3" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <circle cx="4" cy="5" r="1.5" />
            <circle cx="10" cy="5" r="1.5" />
            <circle cx="16" cy="5" r="1.5" />
            <circle cx="4" cy="11" r="1.5" />
            <circle cx="10" cy="11" r="1.5" />
            <circle cx="16" cy="11" r="1.5" />
          </svg>
        </span>
      }
    >
      <div data-ui="tags-config-page-region-002" className="flex items-center gap-1 ml-1">
        <button
          type="button"
          className="btn-taratic btn-ghost-dark btn-xs"
          onClick={() => onEdit?.(tag)}
          aria-label={t('productsPage.item.buttons.edit')}
          title={t('productsPage.item.buttons.edit')}
        >
          <svg className="w-3 h-3" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path d="M4 13.5V16h2.5l7.35-7.35-2.5-2.5L4 13.5zm9.85-8.35l2.5 2.5 1.15-1.15a1 1 0 000-1.41l-1.59-1.59a1 1 0 00-1.41 0L13.85 5.15z" />
          </svg>
        </button>
        <button
          type="button"
          className="btn-taratic btn-ghost-dark btn-xs text-red-600"
          onClick={() => onDelete?.(tag)}
          aria-label={t('productsPage.item.buttons.delete')}
          title={t('productsPage.item.buttons.delete')}
        >
          <svg className="w-3 h-3" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path d="M6 7h1v9H6V7zm3 0h1v9H9V7zm3 0h1v9h-1V7z" />
            <path d="M5 6h10v1H5V6zm2-2h6v1H7V4z" />
            <path d="M8 4h4l1 1h3v1H4V5h3l1-1z" />
          </svg>
        </button>
        {/* Mobile/tablet move button (desktop users can drag) */}
        <button
          type="button"
          className="btn-taratic btn-ghost-dark btn-xs lg:hidden"
          onClick={() => onMove?.(tag)}
          aria-label={t('tagsConfigPage.buttons.move')}
          title={t('tagsConfigPage.buttons.move')}
        >
          {t('tagsConfigPage.buttons.move')}
        </button>
      </div>
    </TagBadge>
  );
};

const FolderNode: React.FC<{
  node: FolderTree;
  expanded: Record<number, boolean>;
  setExpanded: (fn: (prev: Record<number, boolean>) => Record<number, boolean>) => void;
  onDropToFolder: (folderId: number | null, ev: React.DragEvent) => void;
  onDragOverFolder: (folderId: number | null, ev: React.DragEvent) => void;
  byId: Map<number, FolderTree>;
  onRequestMove?: (item: DragItem) => void;
  onEditFolder?: (folder: TagFolder) => void;
  onDeleteFolder?: (node: FolderTree) => void;
  onEditTag?: (tag: Tag) => void;
  onDeleteTag?: (tag: Tag) => void;
  onCreateTagIn?: (folderId: number) => void;
  onCreateFolderIn?: (parentId: number) => void;
}> = ({
  node,
  expanded,
  setExpanded,
  onDropToFolder,
  onDragOverFolder,
  byId,
  onRequestMove,
  onEditFolder,
  onDeleteFolder,
  onEditTag,
  onDeleteTag,
  onCreateTagIn,
  onCreateFolderIn
}) => {
    const { t } = useTranslation();
    const id = Number(node.folder.id);
    const isOpen = !!expanded[id];
    const [over, setOver] = useState(false);

    return (
      <div data-ui="tags-config-page-region-003" className="pl-3">
        <div data-ui="tags-config-page-region-004"
          className={`flex items-center gap-2 px-2 py-1 rounded cursor-pointer ${over ? 'bg-blue-900/30 text-blue-200 border-blue-500/50' : 'hover:bg-black/10 text-white/80'} flex-wrap sm:flex-nowrap min-w-0`}
          draggable
          onDragStart={(e) => {
            e.dataTransfer.setData('text/plain', `folder:${id}`);
            e.dataTransfer.effectAllowed = 'move';
          }}
          onDragOver={(e) => {
            onDragOverFolder(id, e);
            setOver(true);
          }}
          onDragLeave={() => setOver(false)}
          onDrop={(e) => {
            setOver(false);
            onDropToFolder(id, e);
          }}
          onClick={() => setExpanded((prev) => ({ ...prev, [id]: !isOpen }))}
        >
          {/* Drag handle icon to indicate draggable */}
          <span className="flex items-center justify-center text-white/40 cursor-grab">
            <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <circle cx="4" cy="5" r="1.5" />
              <circle cx="10" cy="5" r="1.5" />
              <circle cx="16" cy="5" r="1.5" />
              <circle cx="4" cy="11" r="1.5" />
              <circle cx="10" cy="11" r="1.5" />
              <circle cx="16" cy="11" r="1.5" />
            </svg>
          </span>
          <span className="w-4 text-white/50">{isOpen ? '▾' : '▸'}</span>
          <span className="font-medium text-white/80 max-w-full overflow-x-auto whitespace-nowrap">📁 {node.folder.name}</span>
          <div data-ui="tags-config-page-region-005" className="flex items-center gap-1 ml-0 sm:ml-auto flex-wrap">
            <button
              type="button"
              className="btn-taratic btn-ghost-dark btn-xs"
              onClick={(e) => {
                e.stopPropagation();
                onCreateTagIn?.(id);
              }}
              aria-label={t('tagsConfigPage.folders.createTagHereTitle')}
              title={t('tagsConfigPage.folders.createTagHereTitle')}
            >
              <i className="fa-solid fa-tag mr-1" />{t('tagsConfigPage.buttons.tagAdd')}
            </button>
            <button
              type="button"
              className="btn-taratic btn-ghost-dark btn-xs"
              onClick={(e) => {
                e.stopPropagation();
                onCreateFolderIn?.(id);
              }}
              aria-label={t('tagsConfigPage.folders.createSubfolderHereTitle')}
              title={t('tagsConfigPage.folders.createSubfolderHereTitle')}
            >
              <i className="fa-solid fa-folder mr-1" /> {t('tagsConfigPage.buttons.folderAdd')}
            </button>
            <button
              type="button"
              className="btn-taratic btn-ghost-dark btn-xs"
              onClick={(e) => {
                e.stopPropagation();
                onEditFolder?.(node.folder);
              }}
              aria-label={t('tagsConfigPage.folders.editFolderTitle')}
              title={t('tagsConfigPage.folders.editFolderTitle')}
            >
              <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path d="M4 13.5V16h2.5l7.35-7.35-2.5-2.5L4 13.5zm9.85-8.35l2.5 2.5 1.15-1.15a1 1 0 000-1.41l-1.59-1.59a1 1 0 00-1.41 0L13.85 5.15z" />
              </svg>
            </button>
            <button
              type="button"
              className="btn-taratic btn-ghost-dark btn-xs text-red-600"
              onClick={(e) => {
                e.stopPropagation();
                onDeleteFolder?.(node);
              }}
              aria-label={t('tagsConfigPage.folders.deleteFolderTitle')}
              title={t('tagsConfigPage.folders.deleteFolderTitle')}
            >
              <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path d="M6 7h1v9H6V7zm3 0h1v9H9V7zm3 0h1v9h-1V7z" />
                <path d="M5 6h10v1H5V6zm2-2h6v1H7V4z" />
                <path d="M8 4h4l1 1h3v1H4V5h3l1-1z" />
              </svg>
            </button>
            {/* Mobile/tablet move button for folders */}
            <button
              type="button"
              className="btn-taratic btn-ghost-dark btn-xs lg:hidden"
              onClick={(e) => {
                e.stopPropagation();
                onRequestMove?.({ kind: 'folder', id });
              }}
              aria-label={t('tagsConfigPage.buttons.move')}
              title={t('tagsConfigPage.buttons.move')}
            >
              {t('tagsConfigPage.buttons.move')}
            </button>
          </div>
        </div>
        {isOpen && (
          <div data-ui="tags-config-page-region-006" className="ml-6 mt-1">
            <div data-ui="tags-config-page-region-007" className="flex flex-wrap">
              {node.tags.map((t) => (
                <TagPill
                  key={t.id}
                  tag={t}
                  onMove={(tag) => onRequestMove?.({ kind: 'tag', id: Number(tag.id) })}
                  onEdit={onEditTag}
                  onDelete={onDeleteTag}
                />
              ))}
            </div>
            <div data-ui="tags-config-page-region-008" className="mt-1">
              {node.children.map((child) => (
                <FolderNode
                  key={Number(child.folder.id)}
                  node={child}
                  expanded={expanded}
                  setExpanded={setExpanded}
                  onDropToFolder={onDropToFolder}
                  onDragOverFolder={onDragOverFolder}
                  byId={byId}
                  onRequestMove={onRequestMove}
                  onEditFolder={onEditFolder}
                  onDeleteFolder={onDeleteFolder}
                  onEditTag={onEditTag}
                  onDeleteTag={onDeleteTag}
                  onCreateTagIn={onCreateTagIn}
                  onCreateFolderIn={onCreateFolderIn}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

const TagsConfigPage: React.FC = () => {
  const { t } = useTranslation();
  const notifications = useNotifications();
  const confirmDelete = useDeleteConfirmation();
  const theme = useAppSelector((state) => state.ui.theme);
  // RTK Query hooks

  const { data: tf = { tags: [], folders: [] }, isFetching: loading, refetch: refreshAll } = useGetTagsWithFoldersQuery();
  const tags: Tag[] = tf.tags || [];
  const folders: TagFolder[] = tf.folders || [];

  const [createTagMutation] = useCreateTagMutation();
  const [updateTagMutation] = useUpdateTagMutation();
  const [deleteTagMutation] = useDeleteTagMutation();
  const [createFolderMutation] = useCreateFolderMutation();
  const [updateFolderMutation] = useUpdateFolderMutation();
  const [deleteFolderMutation] = useDeleteFolderMutation();
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});
  const [rootOver, setRootOver] = useState<boolean>(false);

  // Mobile/tablet move modal state
  const [showMove, setShowMove] = useState<boolean>(false);
  const [moveItem, setMoveItem] = useState<DragItem | null>(null);
  const [destFolderId, setDestFolderId] = useState<number | null>(null);
  const [destSearch, setDestSearch] = useState<string>('');

  // Edit modal state (for tags and folders)
  const [showEdit, setShowEdit] = useState<boolean>(false);
  const [editKind, setEditKind] = useState<'tag' | 'folder' | null>(null);
  const [editName, setEditName] = useState<string>('');
  const [editColor, setEditColor] = useState<string>('#888888'); // tags only
  const [editTagRef, setEditTagRef] = useState<Tag | null>(null);
  const [editFolderRef, setEditFolderRef] = useState<TagFolder | null>(null);

  // Create modal state (for "Tag +" and "Carpeta +")
  const [showCreate, setShowCreate] = useState<boolean>(false);
  const [createKind, setCreateKind] = useState<'tag' | 'folder' | null>(null);
  const [createName, setCreateName] = useState<string>('');
  const [createColor, setCreateColor] = useState<string>('#888888'); // tags only
  const [createTargetFolderId, setCreateTargetFolderId] = useState<number | null>(null);

  const { roots, byId } = useMemo(() => buildTree(folders, tags), [folders, tags]);
  const rootTags = useMemo(() => tags.filter((t) => t.folder_id == null), [tags]);

  // Flatten folders for destination selector (indentation via level)
  const folderOptions = useMemo(() => {
    const out: Array<{ id: number; name: string; level: number }> = [];
    const visit = (nodes: FolderTree[], level: number) => {
      for (const n of nodes) {
        out.push({ id: Number(n.folder.id), name: String(n.folder.name), level });
        if (Array.isArray(n.children) && n.children.length > 0) visit(n.children, level + 1);
      }
    };
    visit(roots, 0);
    return out;
  }, [roots]);

  const filteredFolderOptions = useMemo(() => {
    const q = destSearch.trim().toLowerCase();
    return folderOptions.filter(opt => !q || opt.name.toLowerCase().includes(q));
  }, [folderOptions, destSearch]);

  // Expand/Collapse all helpers
  const expandAll = useCallback(() => {
    try {
      const next: Record<number, boolean> = {};
      for (const f of folders) next[Number(f.id)] = true;
      setExpanded(next);
    } catch { }
  }, [folders]);
  const collapseAll = useCallback(() => {
    setExpanded({});
  }, []);

  // Whether all folders are currently expanded
  const areAllExpanded = useMemo(() => {
    try {
      if (!folders || folders.length === 0) return false;
      for (const f of folders) {
        if (!expanded[Number(f.id)]) return false;
      }
      return true;
    } catch {
      return false;
    }
  }, [folders, expanded]);

  // Parent/name maps to compute folder paths and to expand ancestors when jumping to a tag
  const parentMap = useMemo(() => {
    const m = new Map<number, number | null>();
    for (const f of folders) m.set(Number(f.id), f.parent_id == null ? null : Number(f.parent_id));
    return m;
  }, [folders]);
  const folderNameMap = useMemo(() => {
    const m = new Map<number, string>();
    for (const f of folders) m.set(Number(f.id), String(f.name));
    return m;
  }, [folders]);

  // Form states for top bar matching ThreadTagsSelector
  const [activeTopTab, setActiveTopTab] = useState<'search' | 'tag' | 'folder'>('search');
  const [newTagName, setNewTagName] = useState('');
  const [newTagColor, setNewTagColor] = useState('#3b82f6');
  const [newTagFolderId, setNewTagFolderId] = useState<number | null>(null);

  const [newFolderName, setNewFolderName] = useState('');
  const [newFolderParentId, setNewFolderParentId] = useState<number | null>(null);

  const PREDEFINED_COLORS = ['#ef4444', '#f97316', '#eab308', '#22c55e', '#3b82f6', '#8b5cf6', '#ec4899', '#64748b'];

  const getFolderPath = useCallback((folderId: number): string => {
    let currentFolder = folders.find((f) => Number(f.id) === Number(folderId));
    if (!currentFolder) return '';
    const path = [currentFolder.name];
    let parentId = currentFolder.parent_id;
    let guard = 0;
    while (parentId != null && guard++ < 100) {
      const parent = folders.find((f) => Number(f.id) === Number(parentId));
      if (parent) {
        path.unshift(parent.name);
        parentId = parent.parent_id;
      } else {
        break;
      }
    }
    return path.join(' / ');
  }, [folders]);

  const ghostFolderOptions = useMemo(() => {
    return folders.map((f: TagFolder) => {
      const folderTags = tags.filter((t: Tag) => Number(t.folder_id) === Number(f.id));
      const previewTags = folderTags.slice(0, 3).map((t: Tag) => t.name).join(', ');
      const suffix = previewTags ? `Tags: ${previewTags}${folderTags.length > 3 ? '...' : ''}` : '';

      return {
        value: String(f.id),
        label: getFolderPath(Number(f.id)),
        subtitle: suffix ? (
          <span className="flex items-center gap-1.5 mt-0.5">
            <FontAwesomeIcon icon={faTags} className="text-[10px] text-gray-500" />
            <span className="truncate max-w-[200px] text-gray-400">{suffix}</span>
          </span>
        ) : undefined,
        icon: <FontAwesomeIcon icon={faFolder} className="text-gray-400" />
      };
    });
  }, [folders, tags, getFolderPath]);

  const handleCreateTag = async () => {
    const name = newTagName.trim();
    if (!name) {
      notifications.warning('Revisa los datos', 'El nombre del tag es obligatorio.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await createTagMutation({
        name,
        color: newTagColor,
        folder_id: newTagFolderId ?? null,
      }).unwrap();
      setNewTagName('');
      setNewTagColor('#3b82f6');
      setNewTagFolderId(null);
      await refreshAll();
      notifications.success('Tag creado', `${name} se añadió correctamente.`);
    } catch (e: any) {
      const message = e?.data?.detail || e?.response?.data?.message || e?.message || t('tagsConfigPage.messages.errorCreatingTag');
      setError(message);
      notifications.error('No se pudo crear el tag', typeof message === 'string' ? message : undefined);
    } finally {
      setSaving(false);
    }
  };

  const handleCreateFolder = async () => {
    const name = newFolderName.trim();
    if (!name) {
      notifications.warning('Revisa los datos', 'El nombre de la carpeta es obligatorio.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await createFolderMutation({
        name,
        parent_id: newFolderParentId ?? null,
      }).unwrap();
      setNewFolderName('');
      setNewFolderParentId(null);
      await refreshAll();
      notifications.success('Carpeta creada', `${name} se añadió correctamente.`);
    } catch (e: any) {
      const message = e?.data?.detail || e?.response?.data?.message || e?.message || t('tagsConfigPage.messages.errorCreatingFolder');
      setError(message);
      notifications.error('No se pudo crear la carpeta', typeof message === 'string' ? message : undefined);
    } finally {
      setSaving(false);
    }
  };

  // Tag search -> results + jump
  const [tagSearch, setTagSearch] = useState<string>('');
  const tagSearchResults = useMemo(() => {
    const q = tagSearch.trim().toLowerCase();
    if (!q) return [] as Array<{ id: number; name: string; folder_id: number | null; path: string; tag: Tag }>;
    const computePath = (fid: number | null) => {
      if (fid == null) return t('tagsConfigPage.root.title');
      const names: string[] = [];
      let cur: number | null = fid;
      let guard = 0;
      while (cur != null && guard++ < 1000) {
        names.unshift(folderNameMap.get(cur) || String(cur));
        cur = parentMap.get(cur) ?? null;
      }
      return names.join(' / ');
    };
    return tags
      .filter(t => String(t.name || '').toLowerCase().includes(q))
      .slice(0, 20)
      .map(t => ({
        id: Number(t.id),
        name: String(t.name || ''),
        folder_id: t.folder_id == null ? null : Number(t.folder_id),
        path: computePath(t.folder_id == null ? null : Number(t.folder_id)),
        tag: t,
      }));
  }, [tagSearch, tags, parentMap, folderNameMap, t]);

  // Search dropdown position & portal tracking
  const searchInputRef = useRef<HTMLDivElement>(null);
  const searchDropdownRef = useRef<HTMLDivElement>(null);
  const [searchDropdownStyle, setSearchDropdownStyle] = useState<React.CSSProperties>({});

  const updateSearchDropdownPosition = useCallback(() => {
    if (searchInputRef.current) {
      const rect = searchInputRef.current.getBoundingClientRect();
      setSearchDropdownStyle({
        top: `${rect.bottom + 6}px`,
        left: `${rect.left}px`,
        width: `${rect.width}px`,
      });
    }
  }, []);

  useEffect(() => {
    if (tagSearch) {
      updateSearchDropdownPosition();
      const handleScroll = (event: Event) => {
        if (searchDropdownRef.current && searchDropdownRef.current.contains(event.target as Node)) {
          return;
        }
        updateSearchDropdownPosition();
      };
      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', updateSearchDropdownPosition);
      return () => {
        window.removeEventListener('scroll', handleScroll, true);
        window.removeEventListener('resize', updateSearchDropdownPosition);
      };
    }
  }, [tagSearch, updateSearchDropdownPosition]);

  const expandAncestorsAndScroll = useCallback((tagId: number) => {
    try {
      const t = tags.find(x => Number(x.id) === Number(tagId));
      if (!t) return;
      const fid = t.folder_id == null ? null : Number(t.folder_id);
      if (fid == null) {
        // Tag in root: just scroll to pill
        setTimeout(() => {
          const el = document.querySelector(`[data-tag-id="${tagId}"]`) as HTMLElement | null;
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            el.classList.add('ring-2', 'ring-blue-300');
            setTimeout(() => el.classList.remove('ring-2', 'ring-blue-300'), 1200);
          }
        }, 100);
        return;
      }
      // Build ancestors chain from root -> fid
      const chain: number[] = [];
      let cur: number | null = fid;
      let guard = 0;
      while (cur != null && guard++ < 1000) {
        chain.unshift(cur);
        cur = parentMap.get(cur) ?? null;
      }
      // Expand all ancestors
      setExpanded(prev => {
        const next = { ...prev };
        for (const id of chain) next[id] = true;
        return next;
      });
      // Scroll after folders render
      setTimeout(() => {
        const el = document.querySelector(`[data-tag-id="${tagId}"]`) as HTMLElement | null;
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.classList.add('ring-2', 'ring-blue-300');
          setTimeout(() => el.classList.remove('ring-2', 'ring-blue-300'), 1200);
        }
      }, 150);
    } catch { }
  }, [tags, parentMap]);

  // Ensure a fresh pull on mount (provider also hydrates from localStorage instantly)
  useEffect(() => {
    void refreshAll();
  }, [refreshAll]);

  const onRequestMove = useCallback((item: DragItem) => {
    setMoveItem(item);
    if (item.kind === 'tag') {
      const t = tags.find(x => Number(x.id) === Number(item.id));
      setDestFolderId((t?.folder_id ?? null) as any);
    } else {
      const f = folders.find(x => Number(x.id) === Number(item.id));
      setDestFolderId((f?.parent_id ?? null) as any);
    }
    setDestSearch('');
    setShowMove(true);
  }, [tags, folders]);

  const confirmMove = useCallback(async () => {
    if (!moveItem) return;
    setSaving(true);
    setError(null);
    try {
      if (moveItem.kind === 'tag') {
        await updateTagMutation({ id: moveItem.id, folder_id: destFolderId ?? null }).unwrap();
      } else {
        // Guard: cannot move a folder into itself or its descendants
        if (destFolderId != null && (Number(destFolderId) === Number(moveItem.id) || isDescendant(Number(moveItem.id), Number(destFolderId), byId))) {
          alert(t('tagsConfigPage.folders.invalidTarget'));
          setSaving(false);
          return;
        }
        await updateFolderMutation({ id: moveItem.id, parent_id: destFolderId ?? null }).unwrap();
      }
      await refreshAll();
      setShowMove(false);
      setMoveItem(null);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || t('tagsConfigPage.messages.errorMoving'));
    } finally {
      setSaving(false);
    }
  }, [moveItem, destFolderId, byId, refreshAll]);

  const parseDrag = (ev: React.DragEvent): DragItem | null => {
    try {
      const raw = ev.dataTransfer.getData('text/plain') || '';
      const [kind, idStr] = raw.split(':');
      const id = Number(idStr);
      if (!Number.isFinite(id)) return null;
      if (kind === 'tag' || kind === 'folder') return { kind, id } as DragItem;
      return null;
    } catch {
      return null;
    }
  };

  const onDragOverFolder = (folderId: number | null, ev: React.DragEvent) => {
    // Always allow drop preview (prevents blocked cursor); validate rules in onDrop
    ev.preventDefault();
    try {
      const item = parseDrag(ev);
      // If invalid (folder -> its own descendant), hint "none"; otherwise "move"
      if (item && item.kind === 'folder' && folderId != null && isDescendant(item.id, folderId, byId)) {
        ev.dataTransfer.dropEffect = 'none';
      } else {
        ev.dataTransfer.dropEffect = 'move';
      }
    } catch {
      ev.dataTransfer.dropEffect = 'move';
    }
  };

  const onDropToFolder = async (folderId: number | null, ev: React.DragEvent) => {
    const item = parseDrag(ev);
    if (!item) return;
    ev.preventDefault();
    setSaving(true);
    setError(null);
    try {
      if (item.kind === 'tag') {
        await updateTagMutation({ id: item.id, folder_id: folderId ?? null }).unwrap();
      } else {
        // move folder under new parent (or root)
        if (folderId != null && isDescendant(item.id, folderId, byId)) return;
        await updateFolderMutation({ id: item.id, parent_id: folderId ?? null }).unwrap();
        // auto expand target
        if (folderId != null) setExpanded((prev) => ({ ...prev, [folderId]: true }));
      }
      await refreshAll();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || t('tagsConfigPage.messages.errorSavingGeneric'));
    } finally {
      setSaving(false);
      setRootOver(false);
    }
  };

  const openCreateTagIn = (folderId: number | null) => {
    setCreateKind('tag');
    setCreateName('');
    setCreateColor('#888888');
    setCreateTargetFolderId(folderId);
    setShowCreate(true);
  };
  const openCreateFolderIn = (parentId: number | null) => {
    setCreateKind('folder');
    setCreateName('');
    setCreateTargetFolderId(parentId);
    setShowCreate(true);
  };

  // Edit/Delete handlers for tags and folders
  const editTag = async (tag: Tag) => {
    // Open modal with current values
    setEditKind('tag');
    setEditTagRef(tag);
    setEditFolderRef(null);
    setEditName(String(tag.name || ''));
    setEditColor(String(tag.color || '#888888'));
    setShowEdit(true);
  };

  const deleteTagHandler = async (tag: Tag) => {
    if (!await confirmDelete({
      title: t('tagsConfigPage.buttons.delete', 'Eliminar etiqueta'),
      message: t('tagsConfigPage.messages.confirmDeleteTag', { name: String(tag.name || '') }),
    })) return;
    setSaving(true);
    setError(null);
    try {
      await deleteTagMutation({ id: Number(tag.id) }).unwrap();
      await refreshAll();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || t('tagsConfigPage.messages.errorDeletingTag'));
    } finally {
      setSaving(false);
    }
  };

  const editFolder = async (folder: TagFolder) => {
    // Open modal with current values (folders currently support name only)
    setEditKind('folder');
    setEditFolderRef(folder);
    setEditTagRef(null);
    setEditName(String(folder.name || ''));
    // Keep color state but not shown for folders (backend does not support folder color yet)
    setShowEdit(true);
  };

  const deleteFolderHandler = async (node: FolderTree) => {
    const parentId = node.folder.parent_id == null ? null : Number(node.folder.parent_id);
    const msg = t('tagsConfigPage.messages.confirmDeleteFolder', { name: String(node.folder.name || '') });
    if (!await confirmDelete({
      title: t('tagsConfigPage.folders.deleteFolderTitle', 'Eliminar carpeta'),
      message: msg,
    })) return;
    setSaving(true);
    setError(null);
    try {
      await deleteFolderMutation({ id: Number(node.folder.id), move_to_id: parentId ?? null }).unwrap();
      await refreshAll();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || t('tagsConfigPage.messages.errorDeletingFolder'));
    } finally {
      setSaving(false);
    }
  };

  const confirmEdit = async () => {
    if (!editKind) return;
    const name = editName.trim();
    if (!name) return;
    setSaving(true);
    setError(null);
    try {
      if (editKind === 'tag' && editTagRef) {
        const patch: { name?: string; color?: string | null } = { name };
        const c = (editColor || '').trim();
        if (c) patch.color = c;
        await updateTagMutation({ id: Number(editTagRef.id), ...patch }).unwrap();
      } else if (editKind === 'folder' && editFolderRef) {
        await updateFolderMutation({ id: Number(editFolderRef.id), name }).unwrap();
      }
      await refreshAll();
      setShowEdit(false);
      setEditKind(null);
      setEditTagRef(null);
      setEditFolderRef(null);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || t('tagsConfigPage.messages.errorSaving'));
    } finally {
      setSaving(false);
    }
  };


  const confirmCreate = async () => {
    if (!createKind) return;
    const kind = createKind;
    const name = createName.trim();
    if (!name) {
      notifications.warning('Revisa los datos', `El nombre ${kind === 'tag' ? 'del tag' : 'de la carpeta'} es obligatorio.`);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (createKind === 'tag') {
        const c = (createColor || '').trim();
        await createTagMutation({ name, color: c || '#888888', folder_id: createTargetFolderId ?? null }).unwrap();
      } else if (createKind === 'folder') {
        await createFolderMutation({ name, parent_id: createTargetFolderId ?? null }).unwrap();
      }
      await refreshAll();
      setShowCreate(false);
      setCreateKind(null);
      setCreateName('');
      setCreateColor('#888888');
      setCreateTargetFolderId(null);
      notifications.success(
        kind === 'tag' ? 'Tag creado' : 'Carpeta creada',
        `${name} se añadió correctamente.`,
      );
    } catch (e: any) {
      const message = e?.data?.detail || e?.response?.data?.message || e?.message || t('tagsConfigPage.messages.errorCreating');
      setError(message);
      notifications.error(
        kind === 'tag' ? 'No se pudo crear el tag' : 'No se pudo crear la carpeta',
        typeof message === 'string' ? message : undefined,
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-ui="tags-config-page" className="page-theme-surface page-theme-tags space-y-4 w-full dark-bg overflow-x-hidden p-1 sm:p-2">
      {/* Primera línea: Header, SlidingPills, Search bar & Creation forms matching ThreadTagsSelector */}
      <div data-ui="tags-config-page-region-009" className="tags-config-surface-glow p-4 bg-black/20 backdrop-filter backdrop-blur-xl rounded-2xl shadow-inner-glow shadow-inner-glow-black-md mb-4 space-y-4">
        {/* Header Title Row */}
        <div data-ui="tags-config-page-region-010" className="flex items-center justify-between flex-wrap gap-4">
          <div data-ui="tags-config-page-region-011" className="min-w-0">
            <h1 className="text-2xl font-bold text-white tracking-wide">{t('tagsConfigPage.title')}</h1>
            <p className="text-xs text-white/60 mt-0.5">
              {t('tagsConfigPage.subtitle')}
            </p>
          </div>
        </div>

        {/* SlidingPills tabs */}
        <div data-ui="tags-config-page-region-012" className="flex justify-start w-full">
          <SlidingPills
            options={[
              { id: 'search', label: 'Buscar Tags', icon: faSearch },
              { id: 'tag', label: 'Nuevo Tag', icon: faPlus },
              { id: 'folder', label: 'Carpeta', icon: faFolderPlus }
            ]}
            activeId={activeTopTab}
            onChange={(id) => setActiveTopTab(id as 'search' | 'tag' | 'folder')}
            variant={theme}
            size="sm"
            className="max-w-md w-full"
          />
        </div>

        {/* Tab 1: Buscar tags */}
        {activeTopTab === 'search' && (
          <div data-ui="tags-config-page-region-013" className="relative w-full max-w-xl" ref={searchInputRef}>
            <div data-ui="tags-config-page-region-014" className="relative">
              <FontAwesomeIcon icon={faSearch} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs pointer-events-none" />
              <input
                type="text"
                value={tagSearch}
                onChange={(e) => setTagSearch(e.target.value)}
                placeholder={t('tagsConfigPage.search.tagJumpPlaceholder')}
                className="w-full bg-black/75 border border-white/10 text-white text-xs pl-9 pr-3 py-2.5 rounded-xl focus:outline-none focus:border-white/30 transition-colors placeholder:text-gray-500 shadow-inner"
              />
            </div>
            {tagSearch && tagSearchResults.length > 0 && typeof document !== 'undefined' && createPortal(
              <div
                data-ui="tags-search-results-popover"
                ref={searchDropdownRef}
                style={searchDropdownStyle}
                className="page-theme-surface page-theme-tags fixed z-[99999] bg-black/60 backdrop-filter backdrop-blur-xl border border-white/20 rounded-2xl shadow-[0_8px_32px_rgba(0,0,0,0.8)] ring-1 ring-white/10 p-2 space-y-1 max-h-60 overflow-y-auto"
              >
                {tagSearchResults.map((r) => (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => {
                      expandAncestorsAndScroll(r.id);
                      setTagSearch('');
                    }}
                    className="w-full text-left p-2 rounded-xl hover:bg-white/10 transition-colors flex flex-col gap-1 items-start cursor-pointer group"
                    title={`${t('tagsConfigPage.ui.goTo', { name: r.name })} (${r.path})`}
                  >
                    <TagBadge tag={r.tag} size="sm" />
                    <div data-ui="tags-config-page-region-015" className="flex items-center gap-1.5 text-xs text-gray-400 pl-0.5 group-hover:text-gray-300 transition-colors">
                      <FontAwesomeIcon icon={faFolder} className="text-[10px] text-gray-500" />
                      <span className="truncate max-w-[300px]">{r.path}</span>
                    </div>
                  </button>
                ))}
              </div>,
              document.body
            )}
            {tagSearch && tagSearchResults.length === 0 && typeof document !== 'undefined' && createPortal(
              <div
                data-ui="tags-search-empty-popover"
                ref={searchDropdownRef}
                style={searchDropdownStyle}
                className="page-theme-surface page-theme-tags fixed z-[99999] bg-black/60 backdrop-filter backdrop-blur-xl border border-white/20 rounded-2xl shadow-[0_8px_32px_rgba(0,0,0,0.8)] ring-1 ring-white/10 p-3 text-xs text-gray-400 text-center"
              >
                {t('tagsConfigPage.search.noResults')}
              </div>,
              document.body
            )}
          </div>
        )}

        {/* Tab 2: Nuevo Tag */}
        {activeTopTab === 'tag' && (
          <div data-ui="tags-config-page-region-016" className="space-y-3 max-w-md">
            <input
              type="text"
              value={newTagName}
              onChange={(e) => setNewTagName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleCreateTag()}
              autoFocus
              placeholder="Nombre del nuevo tag..."
              className="w-full bg-black/75 border border-white/10 text-white text-xs px-3 py-2.5 rounded-xl focus:outline-none focus:border-white/30 transition-colors placeholder:text-gray-500"
            />

            {folders.length > 0 && (
              <div data-ui="tags-config-page-region-017" className="relative w-full z-30">
                <GhostSelect
                  options={ghostFolderOptions}
                  value={newTagFolderId ? String(newTagFolderId) : ''}
                  onChange={(val) => setNewTagFolderId(val ? Number(val) : null)}
                  multiple={false}
                  placeholder="Sin carpeta (Opcional)"
                  disabled={saving}
                  className="w-full text-xs"
                />
              </div>
            )}

            <div data-ui="tags-config-page-region-018" className="flex flex-col gap-2">
              <div data-ui="tags-config-page-region-019" className="flex gap-1.5 justify-start py-1 flex-wrap">
                {PREDEFINED_COLORS.map((c) => (
                  <button
                    key={c}
                    onClick={() => setNewTagColor(c)}
                    className={`w-5 h-5 rounded-full !border-2 !p-0 ${newTagColor === c ? '!border-white scale-110 shadow-[0_0_8px_rgba(255,255,255,0.5)]' : '!border-transparent opacity-70 hover:opacity-100'}`}
                    style={{ backgroundColor: c }}
                    title={c}
                    type="button"
                  />
                ))}
              </div>
              <div data-ui="tags-config-page-region-020" className="flex items-center gap-2">
                <label
                  className="w-6 h-6 rounded-full border border-white/20 shrink-0 overflow-hidden cursor-pointer relative flex items-center justify-center hover:scale-105 transition-transform"
                >
                  <div data-ui="tags-config-page-region-021" className="absolute inset-0 pointer-events-none" style={{ backgroundColor: newTagColor }} />
                  <input
                    type="color"
                    value={newTagColor}
                    onChange={(e) => setNewTagColor(e.target.value)}
                    className="opacity-0 w-10 h-10 cursor-pointer absolute"
                  />
                </label>
                <input
                  type="text"
                  value={newTagColor}
                  onChange={(e) => setNewTagColor(e.target.value)}
                  placeholder="#000000"
                  className="flex-1 bg-black/75 border border-white/10 text-white text-xs px-2.5 py-1.5 rounded-lg focus:outline-none focus:border-white/30 transition-colors uppercase"
                />
              </div>
            </div>

            <BtnTaratic color="blue"
              onClick={handleCreateTag}
              disabled={saving || !newTagName.trim()}
              className="disabled-action w-full flex items-center justify-center gap-2 disabled:pointer-events-auto"
            >
              <FontAwesomeIcon icon={faCheck} /> Crear Tag
            </BtnTaratic>
          </div>
        )}

        {/* Tab 3: Nueva Carpeta */}
        {activeTopTab === 'folder' && (
          <div data-ui="tags-config-page-region-022" className="space-y-3 max-w-md">
            <input
              type="text"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleCreateFolder()}
              autoFocus
              placeholder="Nombre de la carpeta..."
              className="w-full bg-black/75 border border-white/10 text-white text-xs px-3 py-2.5 rounded-xl focus:outline-none focus:border-white/30 transition-colors placeholder:text-gray-500"
            />

            {folders.length > 0 && (
              <div data-ui="tags-config-page-region-023" className="relative w-full z-30">
                <GhostSelect
                  options={ghostFolderOptions}
                  value={newFolderParentId ? String(newFolderParentId) : ''}
                  onChange={(val) => setNewFolderParentId(val ? Number(val) : null)}
                  multiple={false}
                  placeholder="Subcarpeta de... (Opcional)"
                  disabled={saving}
                  className="w-full text-xs"
                />
              </div>
            )}

            <BtnTaratic color="blue"
              onClick={handleCreateFolder}
              disabled={saving || !newFolderName.trim()}
              className="disabled-action w-full flex items-center justify-center gap-2 disabled:pointer-events-auto"
            >
              <FontAwesomeIcon icon={faCheck} /> Crear Carpeta
            </BtnTaratic>
          </div>
        )}
      </div>
      {error && <div data-ui="tags-config-page-region-024" className="mb-3 text-sm text-red-600">{error}</div>}
      {saving && <div data-ui="tags-config-page-region-025" className="mb-3 text-sm text-blue-600">{t('tagsConfigPage.messages.saving')}</div>}
      {loading ? (
        <div data-ui="tags-config-page-region-026" className="text-white/50">{t('tagsConfigPage.messages.loading')}</div>
      ) : (
        <div data-ui="tags-config-page-region-027" className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div data-ui="tags-config-page-region-028" className="lg:col-span-1">
            <DropZone
              label={t('tagsConfigPage.root.dropHere')}
              active={rootOver}
              onDragOver={(e) => {
                onDragOverFolder(null, e);
                setRootOver(true);
              }}
              onDragLeave={() => setRootOver(false)}
              onDrop={(e) => onDropToFolder(null, e)}
            />
            <div data-ui="tags-config-page-region-029" className="tags-config-surface-glow p-4 bg-black/20 backdrop-filter backdrop-blur-xl rounded-2xl shadow-inner-glow shadow-inner-glow-black-md">
              <div data-ui="tags-config-page-region-030" className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <div data-ui="tags-config-page-region-031" className="text-sm font-medium text-white/80">{t('tagsConfigPage.root.title')}</div>
                <button
                  type="button"
                  className="btn-taratic btn-ghost-dark btn-xs"
                  onClick={() => openCreateTagIn(null)}
                  title={t('tagsConfigPage.root.createTagInRootTitle')}
                  aria-label={t('tagsConfigPage.root.createTagInRootTitle')}
                >
                  <i className="fa-solid fa-tag mr-1" />{t('tagsConfigPage.buttons.tagAdd')}
                </button>
              </div>
              <div data-ui="tags-config-page-region-032" className="flex flex-wrap">
                {rootTags.length === 0 ? (
                  <span className="text-xs text-white/40">{t('tagsConfigPage.root.empty')}</span>
                ) : (
                  rootTags.map((t) => (
                    <TagPill
                      key={t.id}
                      tag={t}
                      onMove={(tag) => onRequestMove({ kind: 'tag', id: Number(tag.id) })}
                      onEdit={editTag}
                      onDelete={deleteTagHandler}
                    />
                  ))
                )}
              </div>
            </div>
          </div>
          <div data-ui="tags-config-page-region-033" className="lg:col-span-2">
            <div data-ui="tags-config-page-region-034" className="tags-config-surface-glow p-4 bg-black/20 backdrop-filter backdrop-blur-xl rounded-2xl shadow-inner-glow shadow-inner-glow-black-md">
              <div data-ui="tags-config-page-region-035" className="flex items-center gap-2 text-sm font-medium text-white/80 mb-2">
                <button
                  type="button"
                  onClick={areAllExpanded ? collapseAll : expandAll}
                  className="cursor-pointer p-1 rounded-lg bg-white/5 hover:bg-white/10 text-white/80 transition-colors shadow-[inset_0_0_10px_rgba(255,255,255,0.1)]"
                  aria-label={areAllExpanded ? t('tagsConfigPage.buttons.collapseAll') : t('tagsConfigPage.buttons.expandAll')}
                  title={areAllExpanded ? t('tagsConfigPage.buttons.collapseAll') : t('tagsConfigPage.buttons.expandAll')}
                >
                  <i className={`fa-solid ${areAllExpanded ? 'fa-angles-up' : 'fa-angles-down'}`} />
                </button>
                <span>{t('tagsConfigPage.folders.title')}</span>
              </div>
              {roots.length === 0 ? (
                <div data-ui="tags-config-page-region-036" className="text-xs text-white/40">{t('tagsConfigPage.folders.none')}</div>
              ) : (
                <div data-ui="tags-config-page-region-037">
                  {roots.map((r) => (
                    <FolderNode
                      key={Number(r.folder.id)}
                      node={r}
                      expanded={expanded}
                      setExpanded={setExpanded}
                      onDropToFolder={onDropToFolder}
                      onDragOverFolder={onDragOverFolder}
                      byId={byId}
                      onRequestMove={onRequestMove}
                      onEditFolder={editFolder}
                      onDeleteFolder={deleteFolderHandler}
                      onEditTag={editTag}
                      onDeleteTag={deleteTagHandler}
                      onCreateTagIn={(folderId) => openCreateTagIn(folderId)}
                      onCreateFolderIn={(parentId) => openCreateFolderIn(parentId)}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Mobile/tablet move modal */}
      {showMove && (
        <div data-ui="tags-config-page-region-038" className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/75">
          <div data-ui="tags-config-modal" className="tags-config-modal w-full sm:max-w-md bg-slate-950/95 text-white backdrop-blur-xl border border-white/15 rounded-t-2xl sm:rounded-2xl shadow-2xl p-5">
            <div data-ui="tags-config-page-region-040" className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-white/90">
                {moveItem?.kind === 'tag' ? t('tagsConfigPage.modals.move.titleTag') : t('tagsConfigPage.modals.move.titleFolder')}
              </h3>
              <button
                type="button"
                data-ui="tags-config-modal-close"
                className="w-9 h-9 flex items-center justify-center rounded-full bg-slate-800 text-white border border-white/10 hover:bg-slate-700"
                onClick={() => setShowMove(false)}
                aria-label={t('tagsConfigPage.ui.close')}
                title={t('tagsConfigPage.ui.close')}
              >
                ×
              </button>
            </div>

            <div data-ui="tags-config-page-region-041" className="space-y-2">
              <input
                type="text"
                value={destSearch}
                onChange={(e) => setDestSearch(e.target.value)}
                placeholder={t('tagsConfigPage.search.destFolderPlaceholder')}
                data-ui="tags-config-modal-input"
                className="w-full px-4 py-3 border border-slate-700 bg-slate-900 text-white placeholder:text-slate-500 rounded-xl text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all shadow-inner"
              />

              <div data-ui="tags-config-page-region-042" className="max-h-64 overflow-y-auto space-y-1 p-2 border border-white/10 rounded-xl bg-black/75 shadow-inner thin-purple-scrollbar">
                <div data-ui="tags-config-page-region-043"
                  role="button"
                  tabIndex={0}
                  onClick={() => setDestFolderId(null)}
                  className={`w-full text-left px-4 py-3 text-sm rounded-lg transition-all cursor-pointer flex items-center gap-3 ${destFolderId == null ? 'bg-blue-600/20 text-blue-300 border border-blue-500/30 shadow-[0_0_15px_rgba(37,99,235,0.15)]' : 'text-white/80 hover:bg-white/10 border border-transparent'}`}
                  title={t('tagsConfigPage.folders.moveToRoot')}
                >
                  <i className={`fa-solid fa-folder-tree ${destFolderId == null ? 'text-blue-400' : 'text-white/40'}`} />
                  <span className="font-medium">{t('tagsConfigPage.folders.moveToRoot')}</span>
                </div>
                {filteredFolderOptions.map((opt) => {
                  const invalid = moveItem?.kind === 'folder' &&
                    (Number(opt.id) === Number(moveItem.id) || isDescendant(Number(moveItem.id), Number(opt.id), byId));
                  const isSelected = Number(destFolderId) === Number(opt.id);
                  return (
                    <div data-ui="tags-config-page-region-044"
                      key={opt.id}
                      role="button"
                      tabIndex={invalid ? -1 : 0}
                      onClick={() => !invalid && setDestFolderId(Number(opt.id))}
                      className={`w-full text-left px-4 py-3 text-sm rounded-lg transition-all flex items-center ${invalid ? 'text-white/30 cursor-not-allowed border border-transparent opacity-50' : isSelected ? 'bg-blue-600/20 text-blue-300 border border-blue-500/30 shadow-[0_0_15px_rgba(37,99,235,0.15)] cursor-pointer' : 'text-white/80 hover:bg-white/10 border border-transparent cursor-pointer'}`}
                      title={invalid ? t('tagsConfigPage.folders.invalidTarget') : t('tagsConfigPage.folders.moveTo', { name: opt.name })}
                    >
                      <div data-ui="tags-config-page-region-045" style={{ width: `${opt.level * 16}px` }} className="shrink-0" />
                      <i className={`fa-solid fa-folder mr-3 shrink-0 ${invalid ? 'text-white/20' : isSelected ? 'text-blue-400' : 'text-yellow-500/80'}`} />
                      <span className="truncate font-medium">{opt.name}</span>
                    </div>
                  );
                })}
              </div>

              <div data-ui="tags-config-page-region-046" className="flex items-center justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowMove(false)}
                  className="btn-taratic btn-ghost-dark py-1"
                >
                  {t('tagsConfigPage.buttons.cancel')}
                </button>
                <button
                  type="button"
                  onClick={() => void confirmMove()}
                  className="disabled-action px-3 py-1.5 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:pointer-events-auto"
                  disabled={saving || !moveItem}
                >
                  {saving ? t('tagsConfigPage.buttons.moving') : t('tagsConfigPage.buttons.move')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit tag/folder modal */}
      {showEdit && (
        <div data-ui="tags-config-page-region-047" className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/75">
          <div data-ui="tags-config-modal" className="tags-config-modal w-full sm:max-w-md bg-slate-950/95 text-white backdrop-blur-xl border border-white/15 rounded-t-2xl sm:rounded-2xl shadow-2xl p-5">
            <div data-ui="tags-config-page-region-049" className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-white/90">
                {editKind === 'tag' ? t('tagsConfigPage.modals.edit.titleTag') : t('tagsConfigPage.modals.edit.titleFolder')}
              </h3>
              <button
                type="button"
                data-ui="tags-config-modal-close"
                className="w-9 h-9 flex items-center justify-center rounded-full bg-slate-800 text-white border border-white/10 hover:bg-slate-700"
                onClick={() => setShowEdit(false)}
                aria-label={t('tagsConfigPage.ui.close')}
                title={t('tagsConfigPage.ui.close')}
              >
                ×
              </button>
            </div>
            <div data-ui="tags-config-page-region-050" className="space-y-3">
              <div data-ui="tags-config-page-region-051">
                <label className="block text-xs text-white/70 mb-1">{t('tagsConfigPage.modals.edit.name')}</label>
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  placeholder={t('tagsConfigPage.modals.edit.name')}
                  data-ui="tags-config-modal-input"
                  className="w-full px-3 py-2.5 border border-slate-700 bg-slate-900 text-white placeholder:text-slate-500 rounded-xl text-sm focus:outline-none focus:border-blue-500"
                />
              </div>
              {editKind === 'tag' && (
                <div data-ui="tags-config-page-region-052">
                  <label className="block text-xs text-white/70 mb-1">{t('tagsConfigPage.modals.edit.color')}</label>
                  <div data-ui="tags-config-page-region-053" className="flex items-center gap-2">
                    <input
                      type="color"
                      value={/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(editColor) ? editColor : '#888888'}
                      onChange={(e) => setEditColor(e.target.value)}
                      className="w-10 h-10 p-0 border border-white/20 rounded bg-black cursor-pointer"
                      aria-label={t('tagsConfigPage.modals.edit.color')}
                      title={t('tagsConfigPage.modals.edit.color')}
                    />
                    <input
                      type="text"
                      value={editColor}
                      onChange={(e) => setEditColor(e.target.value)}
                      placeholder="#888888"
                      data-ui="tags-config-modal-input"
                      className="flex-1 px-3 py-2 border border-slate-700 bg-slate-900 text-white placeholder:text-slate-500 rounded-xl text-sm focus:outline-none focus:border-blue-500"
                    />
                    <span
                      className="inline-block w-4 h-4 rounded-full border"
                      style={{ backgroundColor: (/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(editColor) ? editColor : '#888888') }}
                    />
                  </div>
                </div>
              )}
              <div data-ui="tags-config-page-region-054" className="flex items-center justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowEdit(false)}
                  className="btn-taratic btn-ghost-dark py-1"
                >
                  {t('tagsConfigPage.buttons.cancel')}
                </button>
                <button
                  type="button"
                  onClick={() => void confirmEdit()}
                  className="btn-taratic btn-green disabled-action px-3 py-1.5 text-sm rounded-lg disabled:opacity-50 disabled:pointer-events-auto"
                  disabled={saving || !editName.trim()}
                >
                  {saving ? t('tagsConfigPage.buttons.saving') : t('tagsConfigPage.buttons.save')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create tag/folder modal */}
      {showCreate && (
        <div data-ui="tags-config-page-region-055" className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/75">
          <div data-ui="tags-config-modal" className="tags-config-modal w-full sm:max-w-md bg-slate-950/95 text-white backdrop-blur-xl border border-white/15 rounded-t-2xl sm:rounded-2xl shadow-2xl p-5">
            <div data-ui="tags-config-page-region-057" className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-white/90">
                {createKind === 'tag' ? t('tagsConfigPage.modals.create.titleTag') : t('tagsConfigPage.modals.create.titleFolder')}
              </h3>
              <button
                type="button"
                data-ui="tags-config-modal-close"
                className="w-9 h-9 flex items-center justify-center rounded-full bg-slate-800 text-white border border-white/10 hover:bg-slate-700"
                onClick={() => setShowCreate(false)}
                aria-label={t('tagsConfigPage.ui.close')}
                title={t('tagsConfigPage.ui.close')}
              >
                ×
              </button>
            </div>
            <div data-ui="tags-config-page-region-058" className="space-y-3">
              <div data-ui="tags-config-page-region-059">
                <label className="block text-xs text-white/70 mb-1">{t('tagsConfigPage.modals.create.name')}</label>
                <input
                  type="text"
                  value={createName}
                  onChange={(e) => setCreateName(e.target.value)}
                  placeholder={createKind === 'tag' ? t('tagsConfigPage.modals.create.tagNamePlaceholder') : t('tagsConfigPage.modals.create.folderNamePlaceholder')}
                  data-ui="tags-config-modal-input"
                  className="w-full px-3 py-2.5 border border-slate-700 bg-slate-900 text-white placeholder:text-slate-500 rounded-xl text-sm focus:outline-none focus:border-blue-500"
                />
              </div>
              {createKind === 'tag' && (
                <div data-ui="tags-config-page-region-060">
                  <label className="block text-xs text-white/70 mb-1">{t('tagsConfigPage.modals.create.color')}</label>
                  <div data-ui="tags-config-page-region-061" className="flex items-center gap-2">
                    <input
                      type="color"
                      value={/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(createColor) ? createColor : '#888888'}
                      onChange={(e) => setCreateColor(e.target.value)}
                      className="w-10 h-10 p-0 border border-white/20 rounded bg-black cursor-pointer"
                      aria-label={t('tagsConfigPage.modals.edit.color')}
                      title={t('tagsConfigPage.modals.edit.color')}
                    />
                    <input
                      type="text"
                      value={createColor}
                      onChange={(e) => setCreateColor(e.target.value)}
                      placeholder="#888888"
                      data-ui="tags-config-modal-input"
                      className="flex-1 px-3 py-2 border border-slate-700 bg-slate-900 text-white placeholder:text-slate-500 rounded-xl text-sm focus:outline-none focus:border-blue-500"
                    />
                    <span
                      className="inline-block w-4 h-4 rounded-full border"
                      style={{ backgroundColor: (/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(createColor) ? createColor : '#888888') }}
                    />
                  </div>
                </div>
              )}
              <div data-ui="tags-config-page-region-062" className="flex items-center justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowCreate(false)}
                  className="btn-taratic btn-ghost-dark py-1"
                >
                  {t('tagsConfigPage.buttons.cancel')}
                </button>
                <button
                  type="button"
                  onClick={() => void confirmCreate()}
                  className="btn-taratic btn-blue disabled-action px-3 py-1.5 text-sm rounded-lg disabled:opacity-50 disabled:pointer-events-auto"
                  disabled={saving || !createName.trim() || !createKind}
                >
                  {saving ? t('tagsConfigPage.buttons.creating') : t('tagsConfigPage.buttons.create')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TagsConfigPage;
